/**
 * Client-side mirror of the backend's prompts.render_skill(), byte-for-byte. Do NOT
 * change headings / separators / empty-state strings without changing the backend too.
 */
import type { Exemplar, Exclusion } from "./types";

// Em dash separating an example/exclusion from its rationale.
const DASH = "—";
const LOOKFOR_EMPTY = "_No example passages yet._";
const EXCLUSIONS_EMPTY = "_None yet — reject an AI suggestion to teach the model what to avoid._";

const collapse = (t?: string): string => (t || "").split(/\s+/).filter(Boolean).join(" ");

export function splitKeywords(keywords?: string): string[] {
  const raw = (keywords || "").replace(/\n/g, ",").split(",");
  const seen = new Set<string>();
  const out: string[] = [];
  for (let k of raw) {
    k = k.trim();
    if (k && !seen.has(k.toLowerCase())) {
      seen.add(k.toLowerCase());
      out.push(k);
    }
  }
  return out;
}

function exemplarBullet(ex: Exemplar): string {
  const text = collapse(ex.text);
  const rationale = collapse(ex.rationale);
  return rationale ? `- "${text}" ${DASH} ${rationale}` : `- "${text}"`;
}

function exclusionBullet(ex: Exclusion): string {
  let snippet = collapse(ex.quote);
  if (snippet.length > 160) snippet = `${snippet.slice(0, 157).replace(/\s+$/, "")}…`;
  let b = `- Do NOT code passages like: "${snippet}"`;
  const reason = collapse(ex.rationale);
  if (reason) b += ` ${DASH} ${reason}`;
  const when = (ex.when || "").trim();
  if (when) b += `  (${when})`;
  return b;
}

export function renderSkill(
  name: string,
  description: string,
  keywords: string,
  exemplars: Exemplar[],
  exclusions: Exclusion[]
): string {
  const kws = splitKeywords(keywords);
  const exs = (exemplars || []).filter((e) => collapse(e.text));
  const exc = (exclusions || []).filter((e) => collapse(e.quote));
  const lines = [
    `# ${name.trim() || "Untitled theme"}`,
    "",
    "## Definition",
    description.trim() || "_No definition provided yet._",
    "",
    "## Keywords",
    ...(kws.length ? kws.map((k) => `- ${k}`) : ["_No signal keywords yet._"]),
    "",
    "## Look for",
    ...(exs.length ? exs.map(exemplarBullet) : [LOOKFOR_EMPTY]),
    "",
    "## Exclusions (learned from feedback)",
    ...(exc.length ? exc.map(exclusionBullet) : [EXCLUSIONS_EMPTY]),
  ];
  return `${lines.join("\n").trim()}\n`;
}

export { collapse };
